import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler, HttpEvent,
  HttpResponse, HttpErrorResponse, HttpHeaders,
} from '@angular/common/http';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { TimeoutError } from 'rxjs';
import { Router } from '@angular/router';
import { ApiLoggerService } from '../services/api-logger.service';
import { AuthService } from '../services/auth.service';

@Injectable()
export class ApiLogInterceptor implements HttpInterceptor {

  // v1.6.318 — Injector used to lazily resolve AuthService on the 401
  // path. Direct constructor injection of AuthService here would create
  // a circular dependency through the HttpClient that AuthService uses.
  // The class import above is type-only; .get() resolves at runtime.
  constructor(
    private logger: ApiLoggerService,
    private router: Router,
    private injector: Injector,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startTime = performance.now();

    // ─── Inject Bearer token + X-Tenant header ─────────
    const token = localStorage.getItem('hpm_token');
    const tenantRaw = localStorage.getItem('hpm_tenant');
    let tenantSlug = '';
    if (tenantRaw) {
      try { tenantSlug = JSON.parse(tenantRaw).slug || ''; } catch {}
    }

    const unauthPaths = ['/login', '/forgot-password', '/reset-password', '/login-by-phone', '/verify-phone-login'];
    const isUnauthEndpoint = unauthPaths.some(p => req.url.includes(p));

    let authReq = req;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (!isUnauthEndpoint && token && token !== 'undefined' && token !== 'null') {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (tenantSlug) { headers['X-Tenant'] = tenantSlug; }
    // Use setHeaders so we never override Content-Type that Angular already set
    authReq = req.clone({ setHeaders: headers });

    // ─── Log request ──────────────────────────────────
    const reqHeaders = this.headersToObject(authReq.headers);
    let reqBody: any = null;
    try {
      reqBody = authReq.body instanceof FormData ? '[FormData]'
        : authReq.body ? JSON.parse(JSON.stringify(authReq.body)) : null;
    } catch { reqBody = authReq.body?.toString() ?? null; }

    const logId = this.logger.createEntry(authReq.method, authReq.urlWithParams, reqHeaders, reqBody);

    return next.handle(authReq).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          const duration = Math.round(performance.now() - startTime);
          const resHeaders = this.headersToObject(event.headers);
          let resBody: any = null;
          try { resBody = event.body ? JSON.parse(JSON.stringify(event.body)) : null; } catch { resBody = event.body?.toString() ?? null; }
          this.logger.completeEntry(logId, event.status, event.statusText, resHeaders, resBody, duration);
        }
      }),
      catchError((error: any) => {
        const duration = Math.round(performance.now() - startTime);

        let errorMessage: string;
        let status: number | null = null;

        if (error instanceof TimeoutError) {
          errorMessage = `Request timed out after ${duration}ms`;
          status = 408;
          this.logger.errorEntry(logId, errorMessage, status, duration);
        } else if (error instanceof HttpErrorResponse) {
          status = error.status;
          errorMessage = error.error instanceof ErrorEvent
            ? `Network Error: ${error.error.message}`
            : `${error.status} ${error.statusText}: ${JSON.stringify(error.error) || error.message}`;
          this.logger.errorEntry(logId, errorMessage, status, duration);

          // ─── 401 → clear auth, redirect to login ──────
          if (error.status === 401 && !this.isThirdPartyCredentialCheck(authReq.url)) {
            // v1.6.318 — use AuthService.clearAuth() so the 401-expiry
            // path also nukes ALL user/tenant-scoped storage and resets
            // the in-memory store-context. Previously this only removed
            // hpm_token + hpm_user, leaking cached state into whoever
            // logs in next on the same device.
            try {
              this.injector.get(AuthService).clearAuth();
            } catch {
              // Fallback: explicit minimal clear if AuthService can't be resolved
              try { localStorage.removeItem('hpm_token'); } catch {}
              try { localStorage.removeItem('hpm_user');  } catch {}
            }
            const currentUrl = this.router.url;
            // v1.6.776 — Include '/auth/impersonate' so the interceptor
            // doesn't pre-empt the ImpersonateComponent's own error
            // handling. Pre-fix any 401 (e.g. an expired impersonation
            // token, or a stray init request firing under no token)
            // navigated to /login BEFORE the component could surface a
            // useful error message — and made the impersonation flow
            // only work when an operator was already authenticated.
            if (!currentUrl.includes('/login') && !currentUrl.includes('/select-instance') && !currentUrl.includes('/auth/impersonate')) {
              this.router.navigate(['/login']);
            }
          }
        } else {
          errorMessage = error?.message || String(error);
          this.logger.errorEntry(logId, errorMessage, null, duration);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * v1.6.1234 — Operator-reported: "marketing > happy texting, something broke,
   * now when people try to login it boots them out."
   *
   * ROOT CAUSE. The Happy Texting Setup tab has a Connect form where the operator
   * types their HAPPY TEXTING credentials; it POSTs to
   * `/v1/tenant/happy-texting/authenticate` (happy-texting-setup.component.ts:82).
   * When those third-party credentials are rejected the backend answers **401**.
   *
   * This interceptor treated EVERY 401 as "your dashboard session expired": it
   * called `AuthService.clearAuth()` — which wipes all user/tenant storage and
   * resets the store context — then redirected to /login. So mistyping a Happy
   * Texting password logged the operator out of the dashboard entirely. That is
   * exactly the reported symptom: a failed login on one form boots you out of
   * the app.
   *
   * FIX. A 401 from a third-party credential-check endpoint means "THOSE
   * credentials were rejected", not "your session is dead". Those endpoints are
   * exempted from the session-clearing path so the owning component can surface
   * its own "credentials rejected" message.
   *
   * Deliberately narrow — a whitelist, not a broadened rule. Every other 401
   * still clears the session. And the failure mode if a genuinely-expired
   * dashboard token happens to hit one of these first is mild and
   * self-correcting: the very next non-exempt request 401s and logs out
   * properly.
   */
  private isThirdPartyCredentialCheck(url: string): boolean {
    const u = String(url ?? '');
    return /\/happy-texting\/authenticate(\?|$)/.test(u);
  }

  private headersToObject(headers: HttpHeaders): Record<string, string> {
    const obj: Record<string, string> = {};
    for (const key of headers.keys()) { obj[key] = headers.get(key) || ''; }
    return obj;
  }
}
